<template>
  <ValidationProvider
    ref="validator"
    rules="required|min:1"
    :name="formName"
  >
    <BaseInput
      v-bind="$attrs"
      v-on="$listeners"
      v-model="input"
      :clearCacheOnEnter="true"
      :errorMessage="disableValidator ? '' : errorMessage"
      :options="options"
      :optionLabelKey="optionLabelKey"
      :styleConfig="styleConfig"
      :unfocusOnEnter="true"
      @type-input="input => fetchSkillSuggestions(input)"
      @focusin="fetchSkillSuggestions(null)"
    >
      <template v-slot:option="props">
        <div :style="style.label">
          {{ props.label }}
        </div>
        <div :style="style.optionNote">
          {{ getOptionNote(props.option) }}
        </div>
      </template>
      <template v-slot:right="props">
        <GoogleMaterialIconPrimary
          v-if="filled && !props.disabled && props.focus"
          content="close"
          @mousedown="clearInput"
          :style="style.closeIcon"
        />
      </template>
      <template v-slot:error="props">
        {{ props.errorMessage }}
      </template>
    </BaseInput>
  </ValidationProvider>
</template>

<script>
  import ApiSkillSimilarity from '@js/api/skill-similarity'
  import axios from 'axios'
  import BaseInput from '@base/v2/base-input'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import { FontStyle, Layout } from '@js/styles'
  import { InputSuggestionCache } from '@js/form-utils'
  import { mapGetters, mapState } from 'vuex'
  import { ValidationProvider } from 'vee-validate'
  import { min, required } from '@js/validators/rules'

  const FORM_HEIGHT = '32px';

  export default {
    name: 'InputSkillPrimary',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change-input',
    },
    components: {
      BaseInput,
      GoogleMaterialIconPrimary,
      ValidationProvider,
    },
    props: {
      vmodelValue: {
        type: String,
        required: false,
        default: ''
      },
      disableValidator: {
        type: Boolean,
        required: false,
        default: false
      },
      errorMessageTimeout: {
        type: Number,
        reuqired: false,
        default: 3000
      },
      formName: {
        type: String,
        required: false,
        default: 'form'
      },
      optionLabelKey: {
        type: String,
        required: false,
        default: 'name'
      },
    },
    data: function () {
      return {
        api: new ApiSkillSimilarity(),
        cache: new InputSuggestionCache(50),
        errorMessage: '',
        input: this.vmodelValue,
        options: [],
        style: {
          closeIcon: {
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
          },
          label: {
            fontSize: FontStyle.size.md,
            fontWeight: FontStyle.weight.lg,
            whiteSpace: 'nowrap',
          },
          optionNote: {
            margin: `0 0 0 ${Layout.space.sm}`,
            fontSize: FontStyle.size.sm,
            fontWeight: FontStyle.weight.md,
            whiteSpace: 'nowrap',
          },
        },
        styleConfig: {
          container: {
            backgroundColor: 'transparent',
            height: 'fit-content',
          },
          dropdown: {
            top: FORM_HEIGHT,
            boxShadow: `0 ${Layout.space.xs} ${Layout.space.md} rgba(0, 0, 0, 0.2)`,
            padding: `${Layout.space.sm} 0`,
          },
          dropdownLeft: {
            width: 0,
          },
          dropdownRight: {
            width: 0,
          },
          form: {
            alignItems: 'center',
            height: FORM_HEIGHT,
          },
          formPlaceholder: {
            height: FORM_HEIGHT,
          },
          error: {
            fontSize: FontStyle.size.xs,
            fontWeight: FontStyle.weight.sm,
            padding: `${Layout.space.sm}`,
            top: '24px',
          },
          input: {
            flex: 1,
            fontSize: FontStyle.size.md,
            fontWeight: {
              '': FontStyle.weight.lg,
              placeholder: FontStyle.weight.md,
            },
            padding: `0 ${Layout.space.sm}`,
          },
          option: {
            alignItems: 'center',
            display: 'flex',
            width: '100%',
          },
          row: {
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
            height: '32px',
            padding: `0 ${Layout.space.sm}`,
            width: '100%',
          },
        },
      };
    },
    created: function () {
      this.api.enableCanceller = true;

      Object.assign(
        this.style.closeIcon,
        {
          color: this.color.dark2(),
        },
      );

      Object.assign(
        this.style.label,
        {
          color: this.color.dark2(),
        },
      );

      Object.assign(
        this.style.optionNote,
        {
          color: this.color.dark3(),
        },
      );

      Object.assign(
        this.styleConfig.container,
        {
          border: {
            '': `solid ${this.color.dark5()}`,
            hover: `solid ${this.color.dark2()}`,
            active: `solid ${this.color.action1()}`,
            disabled: 'none',
            error: `solid ${this.color.warning1()}`,
          },
          borderWidth: {
            '': '0 0 1px 0',
            disabled: 'none',
          },
        },
      );

      Object.assign(
        this.styleConfig.dropdown,
        {
          backgroundColor: this.color.light1(),
        },
      );

      Object.assign(
        this.styleConfig.error,
        {
          color: this.color.warning1(),
        },
      );

      Object.assign(
        this.styleConfig.input,
        {
          color: {
            '': this.color.dark2(),
            disabled: this.color.dark3(),
            placeholder: this.color.dark4(),
          },
        },
      );

      Object.assign(
        this.styleConfig.row,
        {
          backgroundColor: {
            '': 'transparent',
            hover: this.color.dark6(),
            selected: this.color.dark5(),
            'selected-hover': this.color.dark6(),
          },
        },
      );
    },
    mounted: function () {
      this.$watch(
        () => this.$refs.validator.errors,
        (newErrors, oldErrors) => {
          this.errorMessage = newErrors.length > 0 ? newErrors[0] : '';
          if (this.errorMessage) {
            setTimeout(() => {
              this.errorMessage = '';
            }, this.errorMessageTimeout);
          }
        },
      );
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      filled: function () {
        return this.input.length > 0;
      },
    },
    methods: {
      clearInput: function () {
        this.input = '';
        this.options = [];
      },
      getOptionNote: function (option) {
        if (option.numTenantUsers === 0) {
          return '';
        }
        const note = `${option.numTenantUsers} user`;
        return option.numTenantUsers > 1 ? note + 's' : note;
      },
      fetchSkillSuggestions: async function (newInput = null) {
        const input = newInput !== null ? newInput.trim() : this.input.trim();

        if (input.length === 0) {
          return;
        } else if (this.cache.hasCacheOf(input)) {
          this.options = this.cache.get(input);
          return;
        }

        try {
          const payload = {
            name: input,
            limit: 10,
            requirePopularity: true,
            strict: input.length === 1,
            usePredefinedSkills: true,
          };
          const res = await this.api.post(payload);
          await this.cache.set(input, res.data);
          this.options = await res.data;
        } catch (error) {
          if (!axios.isCancel(error)) {
            console.error(error);
          }
        }
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.input = newVal;
      },
    },
  };
</script>
